import { Layout } from '@components/common'
import { ProductItem } from '@components/common/ProductTile/ProductTilePlp'
import type {
    GetStaticPropsContext,
    GetStaticPropsResult,
    InferGetStaticPropsType
} from 'next'
import s from '@pagestyles/Cms.module.scss'

import { viewCategoryEvent } from '@lib/utils/exponeaTrackingEvents'
import { PLPCartProvider } from '@lib/context/PLPCartContext'
import { WishlistProvider } from '@lib/context/WishlistContext'
import getCMSPage from '@pylot-data/api/operations/get-cms'
import { RichContent } from '@corratech/pylot-rich-content'
import { PLP_CONFIG } from '@pylot/constants/plp'
import type { ProductInterface } from '@pylot/pylotschema'
import React, { ReactElement, useEffect, useMemo } from 'react'
import type { CmsPage } from '@pylot-data/pylotschema'
import { serverSideTranslations } from 'next-i18next/pages/serverSideTranslations'
import { useCmsPage } from '@pylot-data/hooks/cms/use-cms-page'
import { GetStaticPaths } from 'next'
import { SEO, PAGE_TYPE } from '@corratech/pylot-seo'
import { Breadcrumbs } from '@corratech/pylot-breadcrumbs'
import { useTranslation } from 'next-i18next/pages'
import { useCmsBreadcrumbs } from '@lib/hooks/useCmsBreadcrumbs'
import useCarousel from '@pylot-data/hooks/cms/use-carousel'
import { TAB_CONTAINER_SELECTOR, TAB_ITEMS_SELECTOR } from '@lib/utils/cms'
import { isMobileScreen } from '@lib/utils/mediaQuery'
import useAccordion from '@pylot-data/hooks/cms/use-accordion'
import useTabsControl from '@pylot-data/hooks/cms/use-tabs-control'
import useSectionsIds from '@pylot-data/hooks/cms/use-sections-ids'
import { optimizeImages } from '@corratech/pylot-seo/src/util/imageOptimizationUtils'
import cn from 'classnames'
import { useUI } from '@corratech/pylot-ui/context'

const ABOUT_US_ID = 'sobre-nosotros'
const METAS_DESAFIOS_ID = 'sostenibilidad/nuestras-metas-y-desafios'
const GIFT_GUIDE_ID = 'guia-de-regalos'

export const getStaticPaths: GetStaticPaths = async () => {
    return {
        // TODO: Make this dynamic
        paths: [],
        fallback: 'blocking'
    }
}

export async function getStaticProps({
    locale,
    params
}: GetStaticPropsContext): Promise<
    GetStaticPropsResult<{
        cmsPageData: CmsPage | null | undefined
        identifier: string
    }>
> {
    const urlPathParts = params!.identifier as string[]
    const identifier = urlPathParts.join('/')

    try {
        const cmsPageData = await getCMSPage({
            identifier: identifier,
            locale
        })

        if (!cmsPageData) {
            return {
                notFound: true
            }
        }

        return {
            props: {
                cmsPageData,
                identifier,
                ...(await serverSideTranslations(locale!, ['common', 'pdp']))
            },
            revalidate: 14400
        }
    } catch (error) {
        // Si el backend falla durante el prerender/ISR, no propagar el error
        // (rompería la página con un 500): servir el shell sin data inicial para
        // que el cliente la cargue dinámicamente (useCmsPage revalida al montar)
        // y reintentar el SSR pronto vía revalidate corto.
        console.error(
            `CMS getStaticProps failed for "${identifier}"; serving shell for client-side fetch`,
            error
        )
        return {
            props: {
                cmsPageData: null,
                identifier,
                ...(await serverSideTranslations(locale!, ['common', 'pdp']))
            },
            revalidate: 60
        }
    }
}

export default function CMSPage({
    cmsPageData,
    identifier
}: InferGetStaticPropsType<typeof getStaticProps>): ReactElement {
    const { t } = useTranslation(['common', 'pdp', 'wishlist'])
    const isMobile = isMobileScreen()
    const { LoadingIndicator } = useUI()

    const { cmsPage = {}, isValidating } = useCmsPage(identifier, {
        revalidateOnFocus: false,
        revalidateOnMount: true,
        initialData: { data: { cmsPage: cmsPageData } }
    })

    const breadcrumbData = useCmsBreadcrumbs(cmsPageData)

    useSectionsIds({
        isValidating,
        currentIdentifier: identifier,
        targetIdentifier: ABOUT_US_ID,
        sectionClassName: 'about-us-section'
    })

    useSectionsIds({
        isValidating,
        currentIdentifier: identifier,
        targetIdentifier: METAS_DESAFIOS_ID,
        sectionClassName: 'metas-desafios-section'
    })

    useSectionsIds({
        isValidating,
        currentIdentifier: identifier,
        targetIdentifier: GIFT_GUIDE_ID,
        sectionClassName: 'gift-guide-section'
    })

    useCarousel(isValidating, {
        isMobile,
        selector: TAB_CONTAINER_SELECTOR,
        slideSelector: TAB_ITEMS_SELECTOR,
        arrowLeftCustomAbsoluteStyle: {
            bottom: '50%',
            left: '0'
        },
        arrowRightCustomAbsoluteStyle: {
            bottom: '50%',
            left: 'calc(100% - 45px)'
        }
    })

    useCarousel(isValidating, {
        isMobile: true,
        selector: '.metas-desafios-index-bar-container',
        slideSelector: '.metas-desafios-index',
        arrowLeftCustomAbsoluteStyle: {
            bottom: '-8%',
            left: '0'
        },
        arrowRightCustomAbsoluteStyle: {
            bottom: '-8%',
            left: 'calc(100% - 45px)'
        }
    })

    useAccordion({ isValidating, identifier: 'sostenibilidad-accordion' })

    useTabsControl({
        isValidating,
        tabItemSelector: '.metas-desafios-index > h5 a',
        keyProp: 'href',
        decorateSelected: true
    })

    useEffect(() => {
        viewCategoryEvent(breadcrumbData)
    }, [identifier, breadcrumbData])

    const CmsProductTile = useMemo(
        () =>
            function CmsProductTile({
                product,
                isInCarousel
            }: {
                product: ProductInterface
                isInCarousel?: boolean
            }): ReactElement {
                return (
                    <ProductItem
                        product={product}
                        imageParam={{ width: 500 }}
                        enableVideo={PLP_CONFIG.ENABLE_VIDEO_GRID_4}
                        isInCarousel={isInCarousel}
                    />
                )
            },
        []
    )

    const isPageLoading =
        !cmsPage?.content && (isValidating || Boolean(cmsPageData?.content))

    if (isPageLoading) return <LoadingIndicator />

    return (
        <>
            <SEO
                preloadImages={{
                    images: [cmsPage?.content!]
                }}
                cmsPage={cmsPage}
                page={PAGE_TYPE.CMS}
            />
            <div className={cn(s['cms-page'], s['main'])}>
                {breadcrumbData && breadcrumbData?.breadcrumbs?.length > 0 ? (
                    <Breadcrumbs
                        category={breadcrumbData}
                        separator="|"
                        urlPrefix={breadcrumbData.urlPrefix}
                        rootText={t('breadcrumbs|Home')}
                    />
                ) : null}
                <PLPCartProvider>
                    <WishlistProvider>
                        <RichContent
                            html={optimizeImages(cmsPage?.content)}
                            ProductTile={CmsProductTile}
                        />
                    </WishlistProvider>
                </PLPCartProvider>
            </div>
        </>
    )
}

CMSPage.Layout = Layout
